/* ==========================================================================
   Michael Vardani — site-wide behaviour
   Custom elements only; every listener is attached in connectedCallback and
   removed in disconnectedCallback so theme-editor section reloads never leave
   duplicates behind. No globals are created beyond the element definitions.
   ========================================================================== */
(function () {
  'use strict';

  var WISHLIST_KEY = 'mv-wishlist-v2';

  /* ---------------------------------------------------------- helpers --- */

  function readStore() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeStore(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode — the session still works, it just will not persist */
    }
    document.dispatchEvent(new CustomEvent('mv:wishlist:change', { detail: { items: list } }));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function trapFocus(container, event) {
    var focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------- announcement bar --- */

  if (!customElements.get('mv-announcement')) {
    class MvAnnouncement extends HTMLElement {
      connectedCallback() {
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-mv-announcement-item]'));
        var seconds = parseFloat(this.dataset.interval || '0');
        if (this.items.length < 2 || !seconds) return;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        this.index = 0;
        this.timer = window.setInterval(this.advance.bind(this), seconds * 1000);
      }

      disconnectedCallback() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      advance() {
        this.items[this.index].removeAttribute('data-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].setAttribute('data-active', '');
      }
    }

    customElements.define('mv-announcement', MvAnnouncement);
  }

  /* -------------------------------------------------------- the header --- */

  if (!customElements.get('mv-header')) {
    class MvHeader extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onWishlistChange = this.onWishlistChange.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('mv:wishlist:change', this.onWishlistChange);

        // Dawn publishes a cart update after every AJAX add; keep our own
        // counter in step without touching the theme's own markup.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        }

        this.onWishlistChange();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('mv:wishlist:change', this.onWishlistChange);
        if (this.unsubscribe) this.unsubscribe();
      }

      onCartUpdate(payload) {
        var count = payload && payload.cartData ? payload.cartData.item_count : null;
        if (count === null || count === undefined) return;
        var bubble = this.querySelector('[data-mv-cart-count]');
        if (!bubble) return;
        bubble.textContent = count;
        bubble.hidden = count < 1;
      }

      onWishlistChange() {
        var bubble = this.querySelector('[data-mv-wish-count]');
        if (!bubble) return;
        var count = readStore().length;
        bubble.textContent = count;
        bubble.hidden = count < 1;
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        this.closeSearch();
        this.closeMobileNav();
      }

      onClick(event) {
        var el = event.target.closest
          ? event.target.closest(
              '[data-mv-search-toggle],[data-mv-search-close],[data-mv-menu-toggle],[data-mv-menu-close],[data-mv-cart-open]'
            )
          : null;
        if (!el) return;

        if (el.hasAttribute('data-mv-search-toggle')) {
          event.preventDefault();
          this.toggleSearch();
        } else if (el.hasAttribute('data-mv-search-close')) {
          event.preventDefault();
          this.closeSearch();
        } else if (el.hasAttribute('data-mv-menu-toggle')) {
          event.preventDefault();
          this.openMobileNav();
        } else if (el.hasAttribute('data-mv-menu-close')) {
          event.preventDefault();
          this.closeMobileNav();
        } else if (el.hasAttribute('data-mv-cart-open')) {
          // Only intercept when the theme's own drawer is present; otherwise
          // the link falls through to /cart as a normal navigation.
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            event.preventDefault();
            drawer.open();
          }
        }
      }

      toggleSearch() {
        var panel = this.querySelector('[data-mv-search-panel]');
        if (!panel) return;
        var willOpen = panel.hidden;
        panel.hidden = !willOpen;
        var toggle = this.querySelector('[data-mv-search-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) {
          var input = panel.querySelector('input');
          if (input) input.focus();
        }
      }

      closeSearch() {
        var panel = this.querySelector('[data-mv-search-panel]');
        if (!panel || panel.hidden) return;
        panel.hidden = true;
        var toggle = this.querySelector('[data-mv-search-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      }

      openMobileNav() {
        var nav = document.querySelector('[data-mv-mobile-nav]');
        if (!nav) return;
        nav.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        var link = nav.querySelector('a');
        if (link) link.focus();
      }

      closeMobileNav() {
        var nav = document.querySelector('[data-mv-mobile-nav]');
        if (!nav || !nav.classList.contains('is-open')) return;
        nav.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        var toggle = this.querySelector('[data-mv-menu-toggle]');
        if (toggle) toggle.focus();
      }
    }

    customElements.define('mv-header', MvHeader);
  }

  /* ------------------------------------------------------ sort dropdown --- */

  if (!customElements.get('mv-sort')) {
    class MvSort extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onOutside = this.onOutside.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onOutside);
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onOutside);
        document.removeEventListener('keydown', this.onKeydown);
      }

      get menu() {
        return this.querySelector('[data-mv-sort-menu]');
      }

      onClick(event) {
        var toggle = event.target.closest('[data-mv-sort-toggle]');
        if (toggle) {
          event.preventDefault();
          this.setOpen(this.menu && this.menu.hidden);
          return;
        }
        var option = event.target.closest('[data-mv-sort-value]');
        if (!option) return;
        event.preventDefault();
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', option.dataset.mvSortValue);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }

      onOutside(event) {
        if (this.contains(event.target)) return;
        this.setOpen(false);
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.setOpen(false);
      }

      setOpen(open) {
        if (!this.menu) return;
        this.menu.hidden = !open;
        var toggle = this.querySelector('[data-mv-sort-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    }

    customElements.define('mv-sort', MvSort);
  }

  /* --------------------------------------------------------- wishlist --- */

  function itemFromButton(button) {
    var host = button.closest('[data-mv-wish-item]') || button;
    var json = host.getAttribute('data-mv-wish-item');
    if (json) {
      try {
        return JSON.parse(json);
      } catch (e) {
        /* fall through to the DOM scrape below */
      }
    }

    // Product page fallback: the heart lives inside <mv-product>, which
    // already carries the product JSON and the rendered title / media.
    var product = button.closest('mv-product');
    if (!product) return null;
    var data = {};
    var script = product.querySelector('[data-mv-product-json]');
    if (script) {
      try {
        data = JSON.parse(script.textContent) || {};
      } catch (e) {
        data = {};
      }
    }
    var title = product.querySelector('.mv-pdp__title');
    var price = product.querySelector('[data-mv-price-current]');
    var image = product.querySelector('[data-mv-media][data-active] img');
    return {
      handle: data.handle,
      url: data.url,
      title: title ? title.textContent.trim() : data.handle,
      price: price ? price.textContent.trim() : '',
      image: image ? image.currentSrc || image.src : '',
    };
  }

  function isSaved(handle) {
    return readStore().some(function (item) {
      return item.handle === handle;
    });
  }

  function syncHearts() {
    var list = readStore();
    var handles = list.map(function (item) {
      return item.handle;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-mv-wish]'), function (button) {
      var item = itemFromButton(button);
      var on = !!(item && handles.indexOf(item.handle) > -1);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-mv-wish]') : null;
    if (!button) return;
    event.preventDefault();
    var item = itemFromButton(button);
    if (!item || !item.handle) return;
    var list = readStore();
    var index = -1;
    list.forEach(function (entry, i) {
      if (entry.handle === item.handle) index = i;
    });
    if (index > -1) list.splice(index, 1);
    else list.push(item);
    writeStore(list);
    button.setAttribute('aria-pressed', index > -1 ? 'false' : 'true');
  });

  document.addEventListener('mv:wishlist:change', syncHearts);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncHearts);
  } else {
    syncHearts();
  }

  if (!customElements.get('mv-wishlist-drawer')) {
    class MvWishlistDrawer extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onChange = this.render.bind(this);
        this.onOpenRequest = this.open.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('mv:wishlist:change', this.onChange);
        document.addEventListener('mv:wishlist:open', this.onOpenRequest);
        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('mv:wishlist:change', this.onChange);
        document.removeEventListener('mv:wishlist:open', this.onOpenRequest);
        document.documentElement.style.overflow = '';
      }

      open() {
        this.opener = document.activeElement;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        var close = this.querySelector('[data-mv-drawer-close]');
        if (close) close.focus();
      }

      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        if (this.opener && this.opener.focus) this.opener.focus();
      }

      onKeydown(event) {
        if (!this.classList.contains('is-open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') trapFocus(this, event);
      }

      onClick(event) {
        var el = event.target.closest('[data-mv-drawer-close],[data-mv-wish-remove]');
        if (!el) return;
        if (el.hasAttribute('data-mv-drawer-close')) {
          event.preventDefault();
          this.close();
          return;
        }
        event.preventDefault();
        var handle = el.getAttribute('data-mv-wish-remove');
        writeStore(
          readStore().filter(function (item) {
            return item.handle !== handle;
          })
        );
      }

      render() {
        var body = this.querySelector('[data-mv-drawer-body]');
        var empty = this.querySelector('[data-mv-drawer-empty]');
        if (!body || !empty) return;
        var list = readStore();
        empty.hidden = list.length > 0;
        body.hidden = list.length === 0;
        body.innerHTML = list
          .map(function (item) {
            var url = escapeHtml(item.url || '#');
            return (
              '<div class="mv-drawer__item">' +
              '<a class="mv-drawer__thumb" href="' + url + '">' +
              (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="76" height="92">' : '') +
              '</a>' +
              '<div class="mv-drawer__item-info">' +
              '<a class="mv-drawer__item-name" href="' + url + '">' + escapeHtml(item.title) + '</a>' +
              '<div class="mv-drawer__item-price">' + escapeHtml(item.price) + '</div>' +
              '<div class="mv-drawer__item-actions">' +
              '<a class="mv-drawer__action" href="' + url + '">View piece</a>' +
              '<button type="button" class="mv-drawer__action mv-drawer__action--muted" data-mv-wish-remove="' +
              escapeHtml(item.handle) + '">Remove</button>' +
              '</div></div></div>'
            );
          })
          .join('');
      }
    }

    customElements.define('mv-wishlist-drawer', MvWishlistDrawer);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-mv-wishlist-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('mv:wishlist:open'));
  });

  /* ------------------------------------------------- newsletter notice --- */

  if (!customElements.get('mv-scroll-to')) {
    class MvScrollTo extends HTMLElement {
      connectedCallback() {
        if (this.dataset.scroll !== 'true') return;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
    }

    customElements.define('mv-scroll-to', MvScrollTo);
  }
})();
